/* ================================================================
   LFJ — YOUTUBE PLAYER ENGINE  (low-level, CP-THEATER)
   ----------------------------------------------------------------
   Thin wrapper around the YouTube IFrame Player API. Loads the API
   once, boots a player with YouTube's own chrome fully hidden
   (controls:0), and hands the live player object back to the
   caller so a fully custom control bar can drive it — that custom
   bar lives one layer up, in lfj-jtv-player.js.

   Also owns the endscreen mask: near the video's real end (last
   ~4s) a pixel-styled overlay covers the frame before YouTube's
   own related-videos grid can render underneath — this still
   matters even with controls hidden, since the grid appears in the
   video frame itself, not in YouTube's control bar.

   USAGE:
     LFJ_YT_MASK.mount(containerEl, videoId, {
       title: 'Episode title',
       onReady:       function(player) { ... },  // player = YT.Player instance
       onStateChange: function(ytState) { ... }, // raw YT.PlayerState value
       onReplay:      function() { ... }         // optional, called on REPLAY tap
     });
     LFJ_YT_MASK.unmount(containerEl);

   Reused by JTV's theater player and available to Theater's own
   outro sequence later — build the masking treatment once.
================================================================ */
(function () {
  if (window.LFJ_YT_MASK) return;

  var API_READY = false;
  var API_LOADING = false;
  var API_CALLBACKS = [];

  function _loadAPI(cb) {
    if (API_READY) { cb(); return; }
    API_CALLBACKS.push(cb);
    if (API_LOADING) return;
    API_LOADING = true;
    var prevCb = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      API_READY = true;
      if (typeof prevCb === 'function') { try { prevCb(); } catch(e){} }
      API_CALLBACKS.forEach(function(fn) { try { fn(); } catch(e){} });
      API_CALLBACKS = [];
    };
    if (!window.YT || !window.YT.Player) {
      var tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      var first = document.getElementsByTagName('script')[0];
      if (first && first.parentNode) first.parentNode.insertBefore(tag, first);
      else document.head.appendChild(tag);
    } else {
      API_READY = true;
      API_CALLBACKS.forEach(function(fn) { try { fn(); } catch(e){} });
      API_CALLBACKS = [];
    }
  }

  function _esc(s) {
    return String(s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function _buildOverlay(container, opts) {
    var ov = document.createElement('div');
    ov.className = 'lfj-ytmask-overlay';
    ov.innerHTML =
      '<div class="lfj-ytmask-inner">' +
        '<span class="lfj-ytmask-brand">LFJ</span>' +
        '<span class="lfj-ytmask-title">' + _esc(opts.title || '') + '</span>' +
        '<button type="button" class="lfj-ytmask-replay">&#8635; REPLAY</button>' +
      '</div>';
    container.appendChild(ov);
    var replayBtn = ov.querySelector('.lfj-ytmask-replay');
    replayBtn.addEventListener('click', function () {
      ov.classList.remove('lfj-ytmask-overlay--show');
      if (opts.player) {
        try { opts.player.seekTo(0); opts.player.playVideo(); } catch(e) {}
      }
      if (typeof opts.onReplay === 'function') { try { opts.onReplay(); } catch(e){} }
    });
    return ov;
  }

  var MOUNTS = new WeakMap();

  function mount(container, videoId, opts) {
    opts = opts || {};
    if (!container || !videoId) return;

    unmount(container);

    var mountId = 'lfj-ytmask-' + Math.random().toString(36).slice(2);
    var playerDiv = document.createElement('div');
    playerDiv.id = mountId;
    playerDiv.className = 'lfj-ytmask-player';
    container.appendChild(playerDiv);

    var overlay = _buildOverlay(container, opts);
    var pollTimer = null;
    var state = { player: null, overlayShown: false };

    _loadAPI(function () {
      try {
        state.player = new window.YT.Player(mountId, {
          videoId: videoId,
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            autoplay: 1,
            rel: 0,
            modestbranding: 1,
            iv_load_policy: 3,   /* no annotations */
            playsinline: 1,
            controls: 0,         /* fully custom control bar owns the UI now */
            disablekb: 1,        /* our own keyboard shortcuts handle this */
            fs: 0                /* our own fullscreen button owns this */
          },
          events: {
            onReady: function (e) {
              opts.player = state.player;
              if (typeof opts.onReady === 'function') { try { opts.onReady(state.player); } catch(err){} }
            },
            onStateChange: function (e) {
              var YTS = window.YT.PlayerState;
              if (typeof opts.onStateChange === 'function') { try { opts.onStateChange(e.data); } catch(err){} }
              if (e.data === YTS.PLAYING) {
                overlay.classList.remove('lfj-ytmask-overlay--show');
                state.overlayShown = false;
                if (!pollTimer) {
                  pollTimer = setInterval(function () {
                    try {
                      var dur = state.player.getDuration();
                      var cur = state.player.getCurrentTime();
                      if (dur > 0 && dur - cur <= 4 && !state.overlayShown) {
                        overlay.classList.add('lfj-ytmask-overlay--show');
                        state.overlayShown = true;
                      }
                    } catch(err) {}
                  }, 500);
                }
              }
              if (e.data === YTS.ENDED) {
                overlay.classList.add('lfj-ytmask-overlay--show');
                state.overlayShown = true;
              }
            }
          }
        });
      } catch (e) { console.warn('[LFJ:ytmask] player init failed', e); }
    });

    MOUNTS.set(container, {
      playerDiv: playerDiv,
      overlay: overlay,
      getPlayer: function () { return state.player; },
      stop: function () {
        if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
        try { if (state.player && state.player.destroy) state.player.destroy(); } catch(e){}
      }
    });
  }

  function unmount(container) {
    if (!container) return;
    var rec = MOUNTS.get(container);
    if (rec) {
      rec.stop();
      MOUNTS.delete(container);
    }
    container.innerHTML = '';
  }

  window.LFJ_YT_MASK = { mount: mount, unmount: unmount };
}());
